import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { type Vendor, VendorInfo } from "@/components/vendor-info";

/** A single metered price line shown inside a {@link FeatureCard}. */
export type PriceLine = {
  /** Sub-feature name, e.g. "Sanction List". */
  label: string;
  /** Billing unit, e.g. "Per check". */
  unit: string;
  /** Pre-formatted price incl. currency, e.g. "0.495 EUR". */
  price: string;
};

type FeatureCardProps = {
  title: string;
  description?: string;
  /** Metered price lines; pass an empty array for a feature with no published price yet. */
  prices: PriceLine[];
  /** The control on the right — a Switch (boolean features) or a ToggleGroup (mode features). */
  control: ReactNode;
  /** The sub-processor(s) delivering this feature, shown explicitly with verified links.
   * Pass an array when a single feature is split across vendors (e.g. OCR + audio). */
  vendor: Vendor | Vendor[];
  /** Shown in place of the price table when no prices are published yet. */
  priceNote?: string;
  /** Pricing caveat shown under the price table, e.g. test-mode-is-free note. */
  footnote?: string;
  error?: string | null;
  success?: string | null;
};

/**
 * Store-style row for one 3rd-party feature: name + description and its auto-saving
 * control in the header, the metered price list, and a sub-processor identity block with
 * verified terms/status/security links. Used for every feature on the "3rd party features"
 * settings page so the rows stay visually consistent.
 */
export function FeatureCard({
  title,
  description,
  prices,
  control,
  vendor,
  priceNote,
  footnote,
  error,
  success,
}: FeatureCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle>{title}</CardTitle>
          {description && (
            <p className="text-xs text-muted-foreground max-w-prose">{description}</p>
          )}
        </div>
        <div className="shrink-0">{control}</div>
      </CardHeader>
      <CardContent className="space-y-3">
        {prices.length > 0 ? (
          <div className="rounded-md border divide-y">
            {prices.map((p) => (
              <div
                key={p.label}
                className="flex items-center justify-between gap-4 px-3 py-2 text-sm"
              >
                <span className="font-medium">{p.label}</span>
                <span className="flex items-center gap-6 text-muted-foreground">
                  <span className="text-xs">{p.unit}</span>
                  <span className="tabular-nums text-foreground w-20 text-right">{p.price}</span>
                </span>
              </div>
            ))}
          </div>
        ) : (
          priceNote && <p className="text-xs text-muted-foreground italic">{priceNote}</p>
        )}

        {footnote && <p className="text-xs text-muted-foreground">{footnote}</p>}

        {/* Sub-processor identity — who actually delivers this feature, + verified links. */}
        <div className="pt-3 border-t space-y-3">
          {(Array.isArray(vendor) ? vendor : [vendor]).map((v) => (
            <VendorInfo key={v.name} vendor={v} />
          ))}
        </div>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
        )}
        {success && (
          <div className="text-sm text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-400 p-3 rounded-md">
            {success}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
